// Imports
import React, { useState, useEffect, useRef } from "react";
import RecModal from "../../RecModal/jsx/index.jsx";
import { useQuiz } from "src/contexts/QuizContext.js";
import { gtmPush } from "src/modules/utils/helpers.js";
import ky from "ky";

function RecUserActions() {
  const { articles } = useQuiz();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPopoverText, setIsPopoverText] = useState("Copy to clipboard.");
  const [emailPopoverText, setEmailPopoverText] = useState(null);
  const [urls, setUrls] = useState({ shareUrl: null, emailUrl: null });
  const emailModalRef = useRef(null);
  const shareModalRef = useRef(null);
  const shareModalLinkRef = useRef(null);
  const [email, setEmail] = useState("");

  // append query string to share url with article ids
  useEffect(() => {
    let ids = [];
    if (articles.length) {
      ids = articles.map((a) => a.id);
    } else if (window?.config?.articles?.length) {
      ids = window?.config?.articles?.map((a) => a.id);
    }
    if (ids)
      setUrls({
        ...urls,
        shareUrl: `${window?.config?.shareUrl}?recommendations=${ids.join(",")}`,
        emailUrl: `${window?.config?.shareUrl}?ids=${ids.join("|")}&completed=true`,
      });
  }, [articles]);

  function openEmailModal() {
    setIsEmailModalOpen(true);
    document.body.style.overflow = "hidden";
    
    // Track email recommendation button click
    gtmPush({
      event: "recommendation_interaction",
      component_name: "button",
      click_text: "Email recommendation",
      click_url: null,
    });
  }

  function closeEmailModal() {
    setIsEmailModalOpen(false);
    document.body.style.overflow = "initial";
  }

  function openShareModal() {
    setIsShareModalOpen(true);
    document.body.style.overflow = "hidden";
    
    // Track share recommendations button click
    gtmPush({
      event: "recommendation_interaction",
      component_name: "button",
      click_text: "Share recommendations",
      click_url: null,
    });
  }

  function closeShareModal() {
    setIsShareModalOpen(false);
    document.body.style.overflow = "initial";
  }

  function handleCloseEmailModal(e) {
    switch (e.type) {
      case "mousedown":
        if (
          emailModalRef.current &&
          !emailModalRef.current.contains(e.target)
        ) {
          setIsEmailModalOpen(false);
          document.body.style.overflow = "initial";
        }

        break;
      case "keydown":
        if (e.key === "Escape") {
          setIsEmailModalOpen(false);
          document.body.style.overflow = "initial";
        }
        break;
    }
  }

  function handleCloseShareModal(e) {
    switch (e.type) {
      case "mousedown":
        if (
          shareModalRef.current &&
          !shareModalRef.current.contains(e.target)
        ) {
          setIsShareModalOpen(false);
          document.body.style.overflow = "initial";
        }

        break;
      case "keydown":
        if (e.key === "Escape") {
          setIsShareModalOpen(false);
          document.body.style.overflow = "initial";
        }
        break;
    }
  }

  const handleCopy = () => {
    const linkInput = shareModalLinkRef.current;

    if (linkInput) {
      navigator.clipboard
        .writeText(linkInput.value)
        .then(() => {
          setIsPopoverText("Copied!");
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    }
  };

  function addEventListeners(eventTypes, element, handler) {
    eventTypes.forEach((eventType) => {
      element.addEventListener(eventType, handler);
    });
  }

  function removeEventListeners(eventTypes, element, handler) {
    eventTypes.forEach((eventType) => {
      element.removeEventListener(eventType, handler);
    });
  }

  useEffect(() => {
    const linkInput = shareModalLinkRef.current;

    const handleMouseOver = () => {
      linkInput.style.opacity = "0.5";
    };

    const handleMouseOut = () => {
      linkInput.style.opacity = "1";
    };

    if (linkInput) {
      addEventListeners(["mouseover"], linkInput, handleMouseOver);
      addEventListeners(["mouseout"], linkInput, handleMouseOut);
    }

    return () => {
      if (linkInput) {
        removeEventListeners(["mouseover"], linkInput, handleMouseOver);
        removeEventListeners(["mouseout"], linkInput, handleMouseOut);
      }
    };
  }, []);

  useEffect(() => {
    addEventListeners(
      ["mousedown", "keydown"],
      document,
      handleCloseEmailModal
    );
    addEventListeners(
      ["mousedown", "keydown"],
      document,
      handleCloseShareModal
    );

    return () => {
      removeEventListeners(
        ["mousedown", "keydown"],
        document,
        handleCloseEmailModal
      );
      removeEventListeners(
        ["mousedown", "keydown"],
        document,
        handleCloseShareModal
      );
    };
  }, []);

  const sendRecommendation = async (event) => {
    event.preventDefault();
    const errorAlert = "Sorry there was a problem sending your recommendation.";
    if (email) {
      setEmailPopoverText("Sending your recommendation...");
      const messageInput = emailModalRef.current;
      messageInput.style.opacity = "1";
      const url = `${urls.emailUrl}&email=${email}`;
      console.log(`url: ${url}`);
      
      try {
        let response = await ky.get(url).text();
        console.log(`response: ${response}`);
        if (response) {
          setEmail("");
          setEmailPopoverText(
            "Your recommendation has been emailed. Please check your spam/junk folder if you've not receieved it."
          );
        } else {
          console.log("[send recommendation matrix] error");
          setEmailPopoverText(errorAlert);
        }
      } catch (error) {
        setEmailPopoverText(errorAlert);
        const serverMessage = await error;
        console.log("[send recommendation matrix] error - " + serverMessage);
      }

      // clear the message
      setTimeout(() => {
        setEmailPopoverText(null);
      }, 4000);
    }
  };

  return (
    <>
      <div className="rec-user-actions">
        <div className="rec-user-actions__header">
          <h3 className="rec-user-actions__heading">
            You can share your recommendations
          </h3>
          <div className="rec-user-actions__cta">
            {/*<button
              type="button"
              role="button"
              className="button button--clear rec-user-actions__cta-button"
              onClick={() => window.print()}
            >
              Download recommendation
            </button>*/}
            <button
              type="button"
              role="button"
              className="button button--primary rec-user-actions__cta-button"
              onClick={openEmailModal}
            >
              Email recommendation
            </button>
            <button
              type="button"
              role="button"
              className="button button--clear rec-user-actions__cta-button"
              onClick={openShareModal}
            >
              Share recommendations
            </button>
          </div>
        </div>
      </div>

      {isEmailModalOpen && (
        <RecModal
          title="Send me my recommendation"
          desc="Enter your email and we will send you your recommendation."
          modalRef={emailModalRef}
          onClose={closeEmailModal}
        >
          <form
            className="rec-modal__form-wrapper"
            onSubmit={sendRecommendation}
          >
            <div className="rec-modal__mail-input">
              <label htmlFor="recEmail" name="recEmail">
                Your Email
              </label>
              <input
                type="email"
                name="recEmail"
                id="recEmail"
                required="required"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailPopoverText && (
                <div className="rec-modal__copy-popover rec-modal__copy-popover--email">
                  {emailPopoverText}
                </div>
              )}
            </div>

            <div className="rec-modal__cta">
              <button
                type="submit"
                role="button"
                className="button--primary rec-user-actions__cta-button modal--submit"
              >
                Send me my recommendation
              </button>
            </div>
          </form>
        </RecModal>
      )}

      {isShareModalOpen && (
        <RecModal
          title="Share this recommendation"
          desc="Click on the link to copy to clipboard."
          modalRef={shareModalRef}
          onClose={closeShareModal}
        >
          <div className="rec-modal__copy-input">
            <input
              type="button"
              value={urls.shareUrl}
              name="recLink"
              id="recLink"
              className="rec-user-actions__copy modal--copy"
              readOnly={true}
              ref={shareModalLinkRef}
              onClick={handleCopy}
            />
            <div className="rec-modal__copy-popover">{isPopoverText}</div>
          </div>
        </RecModal>
      )}
    </>
  );
}

export default RecUserActions;
